'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { useDiscoverStore, useLangDataStore, useUserStore } from '@/utils/util_states';
import { applyMultiLangToPuzzles } from '@/utils/util_puzzles';
import { getPuzzleDifficultyLevel } from '@/utils/puzzle-display';
import type { PublicPuzzle, Puzzle, PuzzleTab } from '@/utils/util_types';

import { FilterChips, defaultFilters, type ActiveFilters } from '@/components/puzzle/filter-chips';
import { PickupRow } from '@/components/puzzle/pickup-row';
import { PuzzleCard } from '@/components/puzzle/puzzle-card';
import { ChatServiceError, resolveOrStartChat } from '@/services/puzzle-chat';
import { languageCodeFromPathname, localizedPublicPath, normalizeLanguageCode } from '@/i18n/config';
import { getLanguageDataByCode } from '@/i18n/language-data';
import { Ev, track, trackGuestLoginTrigger } from '@/utils/util_analytics';
import { AnnouncementDialog } from '@/components/discover/announcement-dialog';
import { useAnnouncement } from '@/components/discover/use-announcement';

export type DiscoverInitialData = {
    latest: PublicPuzzle[];
    hotToday: PublicPuzzle[];
    hotWeek: PublicPuzzle[];
    pickup: PublicPuzzle[];
};

type DiscoverClientProps = {
    initialData: DiscoverInitialData;
    initialLanguageCode: string;
};

function matchesFacets(puzzle: Puzzle, filters: ActiveFilters): boolean {
    if (filters.origin.length > 0 && !filters.origin.includes(puzzle.origin_type)) return false;

    if (filters.themes.length > 0) {
        const themes = (puzzle.themes ?? '').split(',').map((theme) => theme.trim()).filter(Boolean);
        if (!filters.themes.some((theme) => themes.includes(theme))) return false;
    }

    if (filters.difficulty.length > 0) {
        const level = getPuzzleDifficultyLevel(puzzle.difficulty);
        if (!level || !filters.difficulty.includes(level)) return false;
    }

    return true;
}

function sortPuzzles(puzzles: Puzzle[], sortBy: ActiveFilters['sortBy']): Puzzle[] {
    const result = [...puzzles];
    if (sortBy === 'newest') {
        result.sort((a, b) => (b.created_at ? Date.parse(b.created_at) : 0) - (a.created_at ? Date.parse(a.created_at) : 0));
    } else if (sortBy === 'most-liked') {
        result.sort((a, b) => (b.likedNum ?? 0) - (a.likedNum ?? 0));
    } else if (sortBy === 'most-played') {
        result.sort((a, b) => (b.chatNum ?? 0) - (a.chatNum ?? 0));
    }
    return result;
}

export default function DiscoverClient({ initialData, initialLanguageCode }: DiscoverClientProps) {
    const router = useRouter();
    const pathname = usePathname();
    const { languageCode, langReady } = useLangDataStore();
    const effectiveLanguageCode = languageCodeFromPathname(pathname)
        ?? (langReady ? languageCode : normalizeLanguageCode(initialLanguageCode));
    const langData = getLanguageDataByCode(effectiveLanguageCode);
    const { isLogin, authReady } = useUserStore();
    const { setPuzzles, setRawPuzzles } = useDiscoverStore();
    const isDiscoverPage = pathname === '/discover' || pathname.endsWith('/discover');
    const announcement = useAnnouncement(isDiscoverPage, langReady);

    const [filters, setFilters] = useState<ActiveFilters>(defaultFilters);
    const [resolvingPuzzleId, setResolvingPuzzleId] = useState<string | null>(null);
    const discoverViewTrackedRef = useRef(false);

    const localized = useMemo(() => ({
        latest: applyMultiLangToPuzzles(initialData.latest, effectiveLanguageCode),
        hotToday: applyMultiLangToPuzzles(initialData.hotToday, effectiveLanguageCode),
        hotWeek: applyMultiLangToPuzzles(initialData.hotWeek, effectiveLanguageCode),
        pickup: applyMultiLangToPuzzles(initialData.pickup, effectiveLanguageCode),
    }), [effectiveLanguageCode, initialData]);

    useEffect(() => {
        if (!discoverViewTrackedRef.current) {
            discoverViewTrackedRef.current = true;
            track(Ev.discover_view);
            if (localized.pickup.length > 0) {
                track(Ev.discover_pickup_view, { puzzle_count: localized.pickup.length });
            }
        }
        setPuzzles('latest', localized.latest);
        setPuzzles('hot-today', localized.hotToday);
        setPuzzles('hot-week', localized.hotWeek);
        setRawPuzzles(initialData.latest);
    }, [initialData.latest, localized, setPuzzles, setRawPuzzles]);

    const filteredPuzzles = useMemo(() => {
        const activeLists: Record<PuzzleTab, Puzzle[]> = {
            latest: localized.latest,
            'hot-today': localized.hotToday,
            'hot-week': localized.hotWeek,
        };
        const matching = activeLists[filters.sort].filter((puzzle) => matchesFacets(puzzle, filters));
        return sortPuzzles(matching, filters.sortBy);
    }, [filters, localized]);

    async function handlePuzzleSelect(puzzle: Puzzle, source: 'discover' | 'pickup' = 'discover') {
        if (!authReady || resolvingPuzzleId || !puzzle.id) return;
        track(source === 'pickup' ? Ev.discover_pickup_click : Ev.discover_click, { puzzle_id: puzzle.id });
        if (!isLogin) {
            trackGuestLoginTrigger(source === 'pickup' ? 'featured_puzzle' : 'discover_puzzle', { source, puzzle_id: puzzle.id });
            toast.info(langData.profile_login_now);
            router.push('/login');
            return;
        }

        setResolvingPuzzleId(puzzle.id);
        try {
            const { chatId } = await resolveOrStartChat(puzzle.id);
            router.push(`/chat/${chatId}`);
        } catch (error) {
            if (error instanceof ChatServiceError && error.code === 'need_login') {
                toast.info(langData.profile_login_now);
                router.push('/login');
            } else {
                console.error('Unable to open puzzle:', error);
                toast.error(langData.chat_load_failed);
            }
        } finally {
            setResolvingPuzzleId(null);
        }
    }

    return (
        <div className="flex min-h-full min-w-0 flex-col">
            <div className="mobile-sticky-below-topbar sticky z-40 border-b border-border/50 bg-background/85 backdrop-blur-xl">
                <FilterChips activeFilters={filters} onFilterChange={setFilters} />
                <div className={process.env.NODE_ENV !== 'production' ? 'flex h-9 items-center justify-end px-4 sm:px-10' : 'h-4'}>
                    {process.env.NODE_ENV !== 'production' && (
                        <button type="button" onClick={announcement.openPreview} className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground transition-colors hover:text-lamp" aria-label="Debug announcement preview">
                            <Megaphone size={13} aria-hidden="true" />DEBUG · Announcement preview
                        </button>
                    )}
                </div>
            </div>

            <div className="min-w-0 flex-1 px-4 py-5 pb-16 sm:px-10">
                <div className="mx-auto w-full min-w-0 max-w-[1200px]">
                    <PickupRow
                        puzzles={localized.pickup}
                        onSelect={(puzzle) => void handlePuzzleSelect(puzzle, 'pickup')}
                        resolvingPuzzleId={resolvingPuzzleId}
                        languageCode={effectiveLanguageCode}
                    />

                    {filteredPuzzles.length === 0 && (
                        <div className="py-20 text-center text-sm text-muted-foreground">
                            <p className="mb-3 text-2xl opacity-40">🔍</p>
                            <p>{langData.discover_no_result}</p>
                        </div>
                    )}

                    {filteredPuzzles.length > 0 && (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 max-[1400px]:lg:grid-cols-2 max-[900px]:grid-cols-1">
                            {filteredPuzzles.map((puzzle, index) => (
                                <PuzzleCard
                                    key={puzzle.id}
                                    puzzle={puzzle}
                                    index={index}
                                    onSelect={(puzzle) => void handlePuzzleSelect(puzzle, 'discover')}
                                    disabled={resolvingPuzzleId === puzzle.id}
                                    href={localizedPublicPath(`/puzzle/${encodeURIComponent(puzzle.id ?? '')}`, effectiveLanguageCode)}
                                    languageCode={effectiveLanguageCode}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <AnnouncementDialog
                announcement={announcement.announcement}
                languageCode={effectiveLanguageCode}
                preview={announcement.preview}
                saving={announcement.saving}
                onConfirm={announcement.confirm}
            />
        </div>
    );
}
